import { MapMarker } from "../../components/map/map.types";

/**
 * Determines if an address/location is assigned or unassigned
 * Unassigned: region is null/undefined OR routeId is null/undefined/empty
 * Assigned: has a valid region OR has a valid routeId
 */
/**
 * Determines if an address is assigned to a route or region
 */
export function isAssignedAddress(address: MapMarker): boolean {
  // Check multiple possible indicators of assignment
  const hasValidRouteId =
    !!address.routeId && address.routeId !== "unassigned-locations";
  const hasRegionId = !!address.region?.id;
  const regionIsUnassigned = address.region?.id === "unassigned";

  // An address is assigned if:
  // 1. It has a valid routeId (not "unassigned-locations")
  // 2. OR it has a region that's not "unassigned" and not null
  const isAssigned = hasValidRouteId || (hasRegionId && !regionIsUnassigned);

  return isAssigned;
}

/**
 * Determines if an address/location is unassigned
 */
export const isUnassignedAddress = (address: MapMarker): boolean => {
  return !isAssignedAddress(address);
};

/**
 * Categorizes a list of addresses into assigned and unassigned
 */
export const categorizeAddresses = (addresses: MapMarker[]) => {
  const assigned = addresses.filter(isAssignedAddress);
  const unassigned = addresses.filter(isUnassignedAddress);

  return {
    assigned,
    unassigned,
    hasAssigned: assigned.length > 0,
    hasUnassigned: unassigned.length > 0,
    hasMixed: assigned.length > 0 && unassigned.length > 0,
  };
};

/**
 * Determines the selection type based on addresses
 */
export type SelectionType = "assigned" | "unassigned" | "mixed" | "empty";

export const getSelectionType = (addresses: MapMarker[]): SelectionType => {
  if (addresses.length === 0) return "empty";

  const { hasAssigned, hasUnassigned, hasMixed } =
    categorizeAddresses(addresses);

  if (hasMixed) return "mixed";
  if (hasAssigned) return "assigned";
  if (hasUnassigned) return "unassigned";

  return "empty";
};
